const PASTAEL_REVIEW_API_URL = 'https://api.judge.me/api/v1/reviews';
const PASTAEL_REVIEW_MAX_FILES = 5;
const PASTAEL_REVIEW_MAX_FILE_BYTES = 5 * 1024 * 1024;

if (!customElements.get('pastael-review-form')) {
  customElements.define(
    'pastael-review-form',
    class PastaelReviewForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-review-form]');
        this.submitButton = this.querySelector('[data-review-submit]');
        this.ratingInput = this.querySelector('[data-review-rating]');
        this.stars = this.querySelectorAll('[data-review-star]');
        this.fileInput = this.querySelector('[data-review-pictures]');
        this.fileStatus = this.querySelector('[data-review-file-status]');
        this.errorEl = this.querySelector('[data-review-error]');
        this.successEl = this.querySelector('[data-review-success]');

        this.onStarClick = this.onStarClick.bind(this);
        this.onStarKeydown = this.onStarKeydown.bind(this);
        this.onStarHover = this.onStarHover.bind(this);
        this.onStarLeave = this.onStarLeave.bind(this);
        this.onFileChange = this.onFileChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.starsWrap = this.querySelector('.pastael-review-form__stars');

        this.stars.forEach((star) => {
          star.addEventListener('click', this.onStarClick);
          star.addEventListener('keydown', this.onStarKeydown);
          star.addEventListener('mouseenter', this.onStarHover);
        });
        this.starsWrap?.addEventListener('mouseleave', this.onStarLeave);
        this.fileInput?.addEventListener('change', this.onFileChange);
        this.form?.addEventListener('submit', this.onSubmit);
      }

      disconnectedCallback() {
        this.stars.forEach((star) => {
          star.removeEventListener('click', this.onStarClick);
          star.removeEventListener('keydown', this.onStarKeydown);
          star.removeEventListener('mouseenter', this.onStarHover);
        });
        this.starsWrap?.removeEventListener('mouseleave', this.onStarLeave);
        this.fileInput?.removeEventListener('change', this.onFileChange);
        this.form?.removeEventListener('submit', this.onSubmit);
      }

      onStarClick(event) {
        const button = event.currentTarget;
        const rating = Number(button.dataset.reviewStar);
        this.setRating(rating);
      }

      onStarKeydown(event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const current = Number(this.ratingInput?.value || 0);
        const next = event.key === 'ArrowRight' ? Math.min(5, current + 1 || 1) : Math.max(1, current - 1);
        this.setRating(next);
        this.querySelector(`[data-review-star="${next}"]`)?.focus();
      }

      setRating(rating, { preview = false } = {}) {
        if (!this.ratingInput) return;
        if (!preview) this.ratingInput.value = String(rating);
        this.stars.forEach((star) => {
          const value = Number(star.dataset.reviewStar);
          const selected = value <= rating;
          star.classList.toggle('is-selected', selected);
          if (!preview) {
            star.setAttribute('aria-checked', selected && value === rating ? 'true' : 'false');
          }
        });
        if (!preview) this.clearError();
      }

      onStarHover(event) {
        this.setRating(Number(event.currentTarget.dataset.reviewStar), { preview: true });
      }

      onStarLeave() {
        this.setRating(Number(this.ratingInput?.value || 0), { preview: true });
      }

      onFileChange() {
        const files = this.getSelectedFiles();
        if (!this.fileStatus) return;

        if (!files.length) {
          this.fileStatus.textContent = '';
          this.fileStatus.hidden = true;
          return;
        }

        this.fileStatus.hidden = false;
        this.fileStatus.textContent = files.map((file) => file.name).join(', ');
      }

      getSelectedFiles() {
        const files = Array.from(this.fileInput?.files || []);
        return files.slice(0, PASTAEL_REVIEW_MAX_FILES);
      }

      async filesToDataUrls(files) {
        const urls = [];
        for (const file of files) {
          if (!file.type.startsWith('image/')) continue;
          if (file.size > PASTAEL_REVIEW_MAX_FILE_BYTES) {
            throw new Error(this.dataset.fileSizeError || 'Each picture must be 5MB or smaller.');
          }
          urls.push(await this.readFileAsDataUrl(file));
        }
        return urls;
      }

      readFileAsDataUrl(file) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ''));
          reader.onerror = () => reject(reader.error || new Error('Could not read file'));
          reader.readAsDataURL(file);
        });
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.hidden = false;
        this.errorEl.textContent = message;
      }

      setSubmitting(isSubmitting) {
        if (!this.submitButton) return;
        this.submitButton.disabled = isSubmitting;
        this.submitButton.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
        const idleLabel = this.submitButton.querySelector('[data-review-submit-label]');
        const busyLabel = this.submitButton.querySelector('[data-review-submit-busy]');
        if (idleLabel) idleLabel.hidden = isSubmitting;
        if (busyLabel) busyLabel.hidden = !isSubmitting;
      }

      buildFormBody(pictureUrls) {
        const data = new FormData(this.form);
        const firstName = String(data.get('first_name') || '').trim();
        const lastName = String(data.get('last_name') || '').trim();
        const email = String(data.get('email') || '').trim();
        const social = String(data.get('social_handle') || '').trim();
        const title = String(data.get('title') || '').trim();
        const body = String(data.get('body') || '').trim();
        const rating = Number(data.get('rating') || 0);
        const productId = Number(data.get('product_id') || 0);
        const shopDomain = this.dataset.shopDomain || '';

        let reviewBody = body;
        if (social) {
          const socialLabel = this.dataset.socialLabel || 'Social media handle';
          reviewBody = `${reviewBody}\n\n${socialLabel}: ${social}`;
        }

        const params = new URLSearchParams();
        params.set('shop_domain', shopDomain);
        params.set('platform', 'shopify');
        params.set('name', [firstName, lastName].filter(Boolean).join(' '));
        params.set('email', email);
        params.set('rating', String(rating));
        params.set('body', reviewBody);

        if (title) params.set('title', title);
        if (productId) params.set('id', String(productId));
        pictureUrls.forEach((url) => params.append('picture_urls[]', url));

        return params;
      }

      extractApiError(responseBody, response) {
        if (responseBody && typeof responseBody.message === 'string' && responseBody.message.trim()) {
          return responseBody.message.trim();
        }
        if (responseBody && typeof responseBody.error === 'string' && responseBody.error.trim()) {
          return responseBody.error.trim();
        }
        if (responseBody?.errors) {
          if (Array.isArray(responseBody.errors)) {
            return responseBody.errors.map(String).join(' ');
          }
          if (typeof responseBody.errors === 'object') {
            return Object.values(responseBody.errors)
              .flat()
              .map(String)
              .join(' ');
          }
        }
        if (response && !response.ok) {
          return `${this.dataset.submitError || 'Your review could not be submitted. Please try again.'} (${response.status})`;
        }
        return '';
      }

      isSubmitSuccess(response, responseBody) {
        if (response.ok) return true;
        const message = String(responseBody?.message || '');
        return /processed in background/i.test(message);
      }

      async postReview(formBody) {
        const response = await fetch(PASTAEL_REVIEW_API_URL, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'content-type': 'application/x-www-form-urlencoded;charset=UTF-8',
          },
          body: formBody.toString(),
        });

        let responseBody = {};
        const raw = await response.text();
        if (raw) {
          try {
            responseBody = JSON.parse(raw);
          } catch {
            responseBody = {};
          }
        }

        return { response, responseBody };
      }

      async onSubmit(event) {
        event.preventDefault();
        this.clearError();

        if (!this.form?.checkValidity()) {
          this.form.reportValidity();
          return;
        }

        const rating = Number(this.ratingInput?.value || 0);
        if (!rating) {
          this.showError(this.dataset.ratingError || 'Please choose a rating.');
          this.stars[0]?.focus();
          return;
        }

        this.setSubmitting(true);

        try {
          const pictureUrls = await this.filesToDataUrls(this.getSelectedFiles());
          let { response, responseBody } = await this.postReview(this.buildFormBody(pictureUrls));

          if (!this.isSubmitSuccess(response, responseBody) && pictureUrls.length) {
            ({ response, responseBody } = await this.postReview(this.buildFormBody([])));
          }

          if (!this.isSubmitSuccess(response, responseBody)) {
            const message =
              this.extractApiError(responseBody, response) ||
              this.dataset.submitError ||
              'Your review could not be submitted. Please try again.';
            throw new Error(message);
          }

          this.form.hidden = true;
          if (this.successEl) this.successEl.hidden = false;
        } catch (error) {
          const fallback = this.dataset.submitError || 'Your review could not be submitted. Please try again.';
          const networkHint = this.dataset.networkError || fallback;
          const message =
            error?.message === 'Failed to fetch' ? networkHint : error?.message || fallback;
          this.showError(message);
        } finally {
          this.setSubmitting(false);
        }
      }
    }
  );
}
